import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useCallback } from "react";
import CopilotButton from "../../../../components/CopilotButton";
import { ExpandableDetails } from "../../../../components/ExpandableDetails";
import { IndustriesFilterChip } from "../../../../components/filters/Industries/IndustriesFilterChip";
import { IndustriesPayload } from "../../../../components/filters/Industries/types";
import { CountriesFilterChip } from "../../../../components/filters/Locations/CountriesFilterChip";
import { LocationsFilterChip } from "../../../../components/filters/Locations/LocationsFilterChip";
import { LocationsPayload } from "../../../../components/filters/Locations/types";
import {
  CopilotWorkflow,
  useCopilotStore,
} from "../../../../store/copilot/CopilotStore";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { SAMConfig, Workspace } from "../../../../types/workspace";
import { ICPSAMDisplay } from "../../../ICP/components/SAMSOMSection/ICPSAMDisplay";
import { IcpDataBanner } from "../../../WorkspaceConfiguration/IcpDataBanner";
import { IcpNoDataBanner } from "../../../WorkspaceConfiguration/IcpNoDataBanner";
import {
  determineIfUnmergeableDataExists,
  mergeIndustryPayloads,
} from "../../../WorkspaceConfiguration/utils";
import { useLocationState } from "./useLocationState";

export const Sam = () => {
  const samConfiguration = useWorkspaceStore(
    (state) => state.draft?.configuration.sam || ({} as SAMConfig)
  );
  const setWorkspace = useWorkspaceStore((state) => state.setDraft);
  const icpSAM = useWorkspaceStore((state) => state.icpSAM);
  const isSamIcpAppliedToConfig = useWorkspaceStore(
    (state) => state.isSamIcpAppliedToConfig
  );
  const isNewIcpAvailableForSam = useWorkspaceStore(
    (state) => state.isNewIcpAvailableForSam
  );
  const markIcpAppliedToSam = useWorkspaceStore(
    (state) => state.markIcpAppliedToSam
  );
  const highlightedElement = useCopilotStore(
    (state) => state.highlightedElement
  );

  const hasIcpSAMData = icpSAM?.industry || icpSAM?.geography;

  // Handle geography updates
  const handleGeographyUpdate = useCallback(
    (geography: SAMConfig["geography"]) => {
      setWorkspace((currentWorkspace) => ({
        ...(currentWorkspace as Workspace),
        configuration: {
          ...(currentWorkspace as Workspace).configuration,
          sam: {
            ...(currentWorkspace as Workspace).configuration.sam,
            geography,
          },
        },
      }));
    },
    [setWorkspace]
  );

  // Use the new location state hook
  const {
    includeLocations,
    excludeLocations,
    handleIncludeLocationsChange,
    handleExcludeLocationsChange,
    overrideWithIcpData,
    mergeWithIcpData,
  } = useLocationState({
    samConfiguration,
    onUpdate: handleGeographyUpdate,
  });

  const { industry } = samConfiguration;

  const selectedIndustries: IndustriesPayload = {
    industryIds: industry.ids,
    sicIds: industry.sicIds,
    naicsIds: industry.naicsIds,
  };

  const selectedExcludedIndustries: IndustriesPayload = {
    industryIds: industry.excludedIds,
    sicIds: industry.excludedSicIds,
    naicsIds: industry.excludedNaicsIds,
  };

  const selectedLocationsForCheck: LocationsPayload = {
    countryAlpha2s: includeLocations.countryAlpha2s,
    regionIds: includeLocations.regionIds,
    stateIds: includeLocations.stateIds,
    selectedLocationType: includeLocations.selectedLocationType,
  };

  const isMergeDisabled = determineIfUnmergeableDataExists(
    selectedIndustries,
    selectedLocationsForCheck
  );

  const handleOnSelectedIndustries = (value: IndustriesPayload) => {
    const { industryIds: ids, sicIds, naicsIds } = value;
    setWorkspace((currentWorkspace) => ({
      ...(currentWorkspace as Workspace),
      configuration: {
        ...(currentWorkspace as Workspace).configuration,
        sam: {
          ...(currentWorkspace as Workspace).configuration.sam,
          industry: {
            ...(currentWorkspace as Workspace).configuration.sam.industry,
            ids,
            sicIds,
            naicsIds,
          },
        },
      },
    }));
  };

  const handleOnSelectedExcludedIndustries = (value: IndustriesPayload) => {
    const { industryIds: excludedIds, sicIds, naicsIds } = value;
    setWorkspace((currentWorkspace) => ({
      ...(currentWorkspace as Workspace),
      configuration: {
        ...(currentWorkspace as Workspace).configuration,
        sam: {
          ...(currentWorkspace as Workspace).configuration.sam,
          industry: {
            ...(currentWorkspace as Workspace).configuration.sam.industry,
            excludedIds,
            excludedSicIds: sicIds,
            excludedNaicsIds: naicsIds,
          },
        },
      },
    }));
  };

  // Currently only industryIds and countryAlpha2s is returned for ICP SAM data
  function handleSamOverrideData() {
    if (icpSAM?.industry) {
      const industryPayload: IndustriesPayload = {
        industryIds: icpSAM.industry.ids || [],
        sicIds: [],
        naicsIds: [],
        excludedIds: [],
        excludedSicIds: [],
        excludedNaicsIds: [],
      };
      handleOnSelectedIndustries(industryPayload);
    }

    if (icpSAM?.geography) {
      // Use the new override method that preserves location type
      overrideWithIcpData(icpSAM.geography.countryIds || []);
    }
    markIcpAppliedToSam();
  }

  // Currently only industryIds and countryAlpha2s is returned for ICP SAM data
  function handleSamMergeData() {
    if (icpSAM?.industry) {
      const mergedIndustryPayload = mergeIndustryPayloads(
        selectedIndustries || ({} as IndustriesPayload),
        icpSAM.industry
      );
      handleOnSelectedIndustries(mergedIndustryPayload);
    }

    if (icpSAM?.geography) {
      // Use the new merge method that preserves location type
      mergeWithIcpData(icpSAM.geography.countryIds || []);
    }
    markIcpAppliedToSam();
  }

  return (
    <>
      {hasIcpSAMData ? (
        <IcpDataBanner
          configType="SAM"
          isIcpAppliedToConfig={isSamIcpAppliedToConfig}
          isNewIcpAvailable={isNewIcpAvailableForSam}
          overrideDataAction={handleSamOverrideData}
          mergeDataAction={handleSamMergeData}
          bannerBody={<ICPSAMDisplay />}
          isMergeDisabled={isMergeDisabled}
        />
      ) : (
        <IcpNoDataBanner />
      )}
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 2, mt: 2, border: "1px solid #e0e0e0" }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "hidden",
            overflowWrap: "break-word",
            wordWrap: "break-word",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              zIndex: 1,
            }}
          >
            <CopilotButton
              id="copilot-sam-button"
              workflow={CopilotWorkflow.SAM}
            />
          </Box>
          <Stack flexDirection="column" gap={1}>
            <Stack
              direction="row"
              gap={0.5}
              flexWrap="wrap"
              maxWidth="100%"
              alignItems="center"
              sx={{ pr: "40px" }}
            >
              <Typography>We sell to companies in these</Typography>
              <IndustriesFilterChip
                id="industries"
                selectedValues={selectedIndustries}
                onValuesChange={handleOnSelectedIndustries}
                highlighted={highlightedElement === "industry_chip"}
              />
              <Typography sx={{ paddingRight: "2px" }}>
                , located in these
              </Typography>
              <LocationsFilterChip
                id="locations"
                selectedValues={includeLocations}
                onValuesChange={handleIncludeLocationsChange}
                highlighted={highlightedElement === "geography_chip"}
              />
            </Stack>
          </Stack>

          <Box sx={{ mt: 2 }}>
            <ExpandableDetails
              id="excluded-filters-details"
              isExpanded={
                selectedExcludedIndustries.industryIds?.length > 0 ||
                selectedExcludedIndustries.sicIds?.length > 0 ||
                selectedExcludedIndustries.naicsIds?.length > 0 ||
                excludeLocations.countryAlpha2s?.length > 0
              }
            >
              <Stack flexDirection="column" gap={1}>
                <Stack
                  direction="row"
                  gap={0.5}
                  flexWrap="wrap"
                  maxWidth="100%"
                  alignItems="center"
                >
                  <Typography>Exclude</Typography>
                  <IndustriesFilterChip
                    id="excluded-industries"
                    selectedValues={selectedExcludedIndustries}
                    onValuesChange={handleOnSelectedExcludedIndustries}
                  />
                  <Typography>and, exclude these</Typography>
                  <CountriesFilterChip
                    id="excluded-countries"
                    selectedCountries={excludeLocations.countryAlpha2s}
                    onCountriesChange={handleExcludeLocationsChange}
                  />
                </Stack>
              </Stack>
            </ExpandableDetails>
          </Box>
        </Box>
      </Paper>
    </>
  );
};
